import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clarifyingQuestion, setClarifyingQuestion] = useState(null);
  const [compareList, setCompareList] = useState([]);
  const [excludedTools, setExcludedTools] = useState([]);
  const [showExcluded, setShowExcluded] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    const category = searchParams.get('category');
    
    if (q) {
      setQuery(q);
      performSearch(q);
    } else if (category) {
      // Use category-specific endpoint
      fetchToolsByCategory(category);
    }
  }, [searchParams]);

  const fetchToolsByCategory = async (category) => {
    setLoading(true);
    setClarifyingQuestion(null);
    
    try {
      const response = await axios.get(`${API}/tools/category/${encodeURIComponent(category)}`);
      setTools(response.data.tools || []);
      setExcludedTools([]);
      setQuery(category); // Show category in search bar
    } catch (error) {
      console.error('Category fetch error:', error);
      toast.error('Failed to load tools for this category.');
      setTools([]);
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async (searchQuery) => {
    setLoading(true);
    setClarifyingQuestion(null);
    
    try {
      // Set a timeout for the search request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await axios.post(`${API}/search`, {
        query: searchQuery
      }, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.data.clarifying_question) {
        setClarifyingQuestion(response.data.clarifying_question);
        setTools([]);
        setExcludedTools([]);
      } else {
        setTools(response.data.tools || []);
        setExcludedTools(response.data.excluded_tools || []);
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        toast.error('Search is taking longer than expected. Please try again.');
      } else {
        console.error('Search error:', error);
        toast.error('Failed to search. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      performSearch(query.trim());
    }
  };

  const handleCompareToggle = (toolId) => {
    if (compareList.includes(toolId)) {
      setCompareList(compareList.filter(id => id !== toolId));
    } else if (compareList.length < 3) {
      setCompareList([...compareList, toolId]);
    } else {
      toast.error('You can only compare up to 3 tools');
    }
  };

  const handleCompare = () => {
    if (compareList.length < 2) {
      toast.error('Select at least 2 tools to compare');
      return;
    }
    navigate(`/compare?ids=${compareList.join(',')}`);
  };

  return (
    <Layout>
      <div className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <form onSubmit={handleSearch} className="mb-12">
            <div className="relative max-w-3xl mx-auto">
              <Input
                type="text"
                placeholder="What do you want to do?"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-14 px-6 pr-14 text-lg bg-gray-50 border-2 border-transparent focus:border-[#0044CC] focus:bg-white rounded-xl w-full transition-all placeholder:text-gray-500"
                data-testid="results-search-input"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-10 w-10 rounded-full p-0"
                data-testid="results-search-button"
              >
                <Search className="h-5 w-5" />
              </Button>
            </div>
          </form>

          {loading && (
            <div className="text-center py-12" data-testid="loading-state">
              <div className="text-xl text-[#333333]">Finding the best tools for you...</div>
            </div>
          )}

          {clarifyingQuestion && (
            <div className="max-w-3xl mx-auto mb-12 p-6 bg-gray-50 border-2 border-gray-200 rounded-2xl" data-testid="clarifying-question">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-[#0044CC] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-black mb-2">Need a bit more information</h3>
                  <p className="text-base text-[#333333]">{clarifyingQuestion}</p>
                </div>
              </div>
            </div>
          )}

          {!loading && tools.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl md:text-4xl font-semibold text-black" data-testid="results-heading">
                  Top Recommendations
                </h2>
                {compareList.length > 0 && (
                  <Button
                    onClick={handleCompare}
                    className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-8 rounded-full font-semibold text-lg transition-colors"
                    data-testid="compare-button"
                  >
                    Compare ({compareList.length})
                  </Button>
                )}
              </div>

              <div className="space-y-6">
                {tools.map((tool, index) => (
                  <div
                    key={tool.id}
                    className="bg-white border-2 border-gray-100 p-8 rounded-2xl hover:border-[#0044CC]/50 transition-colors"
                    data-testid={`tool-card-${index}`}
                  >
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-2xl md:text-3xl font-semibold text-black">
                            {tool.name}
                          </h3>
                          <span className="px-3 py-1 bg-gray-100 text-black text-sm font-medium rounded-full">
                            {tool.primary_category}
                          </span>
                          {tool.data_staleness_flag && (
                            <span className="px-3 py-1 bg-yellow-100 text-black text-sm font-medium rounded-full" data-testid="stale-data-badge">
                              May be out of date
                            </span>
                          )}
                        </div>
                        
                        <p className="text-base md:text-lg leading-relaxed text-[#333333] mb-4">
                          {tool.description_plain}
                        </p>

                        <div className="bg-blue-50 border-2 border-blue-100 p-4 rounded-xl mb-4">
                          <p className="text-base text-black">
                            <strong>Why recommended:</strong> Matches your goal, {tool.pricing_model.toLowerCase()} pricing, suitable for {tool.skill_level.toLowerCase()} users.
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-6 text-sm text-[#333333] mb-4">
                          <div>
                            <strong className="text-black">Pricing:</strong> {tool.pricing_model}
                            {tool.starting_price && ` from ${tool.starting_price}`}
                          </div>
                          <div>
                            <strong className="text-black">Skill Level:</strong> {tool.skill_level}
                          </div>
                          <div>
                            <strong className="text-black">Platforms:</strong> {tool.supported_platforms.join(', ')}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-4">
                          {tool.tags.slice(0, 5).map((tag, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 bg-gray-100 text-black text-sm rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-6">
                      <Button
                        onClick={() => navigate(`/tool/${tool.id}`)}
                        className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-8 rounded-full font-semibold text-lg transition-colors"
                        data-testid={`view-details-button-${index}`}
                      >
                        View Details
                      </Button>
                      <Button
                        onClick={() => window.open(tool.official_website, '_blank')}
                        className="bg-transparent border-2 border-[#0044CC] text-[#0044CC] hover:bg-[#0044CC]/5 h-12 px-8 rounded-full font-semibold text-lg transition-colors"
                        data-testid={`visit-website-button-${index}`}
                      >
                        Visit Website
                      </Button>
                      <Button
                        onClick={() => handleCompareToggle(tool.id)}
                        className={`${
                          compareList.includes(tool.id)
                            ? 'bg-[#0044CC] text-white'
                            : 'bg-transparent border-2 border-gray-300 text-black hover:bg-gray-100'
                        } h-12 px-8 rounded-full font-semibold text-lg transition-colors`}
                        data-testid={`compare-toggle-button-${index}`}
                      >
                        {compareList.includes(tool.id) ? 'Added to Compare' : 'Add to Compare'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && tools.length > 0 && excludedTools.length > 0 && (
            <div className="mt-16">
              <p className="text-sm text-[#333333] mb-4 text-center">
                AI Atlas filters tools based on native capability support and structured intent matching to ensure relevance.
              </p>
              
              <button
                onClick={() => setShowExcluded(!showExcluded)}
                className="w-full bg-gray-50 border-2 border-gray-200 p-6 rounded-2xl hover:border-gray-300 transition-colors text-left focus-visible-ring"
                data-testid="excluded-tools-toggle"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Search className="h-5 w-5 text-[#333333]" />
                    <h3 className="text-xl font-semibold text-black">
                      Why were some tools excluded?
                    </h3>
                  </div>
                  {showExcluded ? (
                    <ChevronUp className="h-5 w-5 text-[#333333]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#333333]" />
                  )}
                </div>
              </button>

              {showExcluded && (
                <div className="mt-4 bg-white border-2 border-gray-100 rounded-2xl p-6 space-y-4" data-testid="excluded-tools-list">
                  <p className="text-sm text-[#333333] mb-4">
                    These tools were filtered out because they don't meet the native capability requirements for your query:
                  </p>
                  {excludedTools.map((excluded, index) => (
                    <div
                      key={index}
                      className="p-4 bg-gray-50 rounded-xl"
                      data-testid={`excluded-tool-${index}`}
                    >
                      <h4 className="text-lg font-semibold text-black mb-2">
                        {excluded.tool_name}
                      </h4>
                      <p className="text-base text-[#333333]">
                        {excluded.exclusion_reason}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && !clarifyingQuestion && tools.length === 0 && (
            <div className="text-center py-12" data-testid="no-results">
              <p className="text-xl text-[#333333]">No tools found. Try a different search term.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ResultsPage;